/**
 * Embed appearance, URL, and snippet contracts.
 *
 * This module is safe on the client: it does not import the style catalog.
 * Target existence (is this slug a real style or tool?) lives in
 * `embed-targets.ts` and is checked on the server before a widget renders.
 */

import { DEFAULT_LOCALE } from "@/lib/i18n/locales";

export const EMBED_KINDS = [
  "generator",
  "style",
  "tool",
  "bibliography",
] as const;
export type EmbedKind = (typeof EMBED_KINDS)[number];

export const EMBED_THEMES = ["light", "dark", "system"] as const;
export type EmbedTheme = (typeof EMBED_THEMES)[number];

export const EMBED_CHROMES = ["branded", "minimal", "bare"] as const;
export type EmbedChrome = (typeof EMBED_CHROMES)[number];

export const EMBED_DENSITIES = ["compact", "comfortable", "spacious"] as const;
export type EmbedDensity = (typeof EMBED_DENSITIES)[number];

export const EMBED_RADII = ["none", "sm", "md", "lg", "xl"] as const;
export type EmbedRadius = (typeof EMBED_RADII)[number];

export const EMBED_ACCENTS = [
  "brand",
  "slate",
  "blue",
  "emerald",
  "violet",
  "rose",
  "amber",
] as const;
export type EmbedAccent = (typeof EMBED_ACCENTS)[number];

export const EMBED_HEIGHT_MIN = 360;
export const EMBED_HEIGHT_MAX = 2400;
export const EMBED_HEIGHT_DEFAULT = 720;
export const EMBED_WIDTH_MIN = 280;
export const EMBED_WIDTH_MAX = 1600;

export const EMBED_MESSAGE_SOURCE = "citationeasy-embed";
export const EMBED_RESIZE_TYPE = "resize";
export const EMBED_SCRIPT_PATH = "/embed.js";

export const EMBED_SITE_URL = "https://www.citationeasy.com";

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const HEX_COLOR_PATTERN = /^#?([0-9a-fA-F]{6})$/;
const LOCALE_CODE_PATTERN = /^[a-z]{2}$/;
const SOURCE_UNSAFE_PATTERN = /[\r\n<>]/;
const HTML_ATTR_ESCAPE = /[&<>"']/g;
const HTML_ATTR_REPLACEMENTS: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

export const EMBED_ACCENT_HEX: Record<Exclude<EmbedAccent, "brand">, string> = {
  slate: "#334155",
  blue: "#2563eb",
  emerald: "#059669",
  violet: "#7c3aed",
  rose: "#e11d48",
  amber: "#d97706",
};

export const EMBED_RADIUS_CSS: Record<EmbedRadius, string> = {
  none: "0px",
  sm: "4px",
  md: "8px",
  lg: "12px",
  xl: "20px",
};

export interface EmbedTarget {
  kind: EmbedKind;
  slug?: string;
}

export interface EmbedAppearance {
  theme: EmbedTheme;
  chrome: EmbedChrome;
  density: EmbedDensity;
  radius: EmbedRadius;
  accent: EmbedAccent;
  /** Six-digit hex without `#`. Used only when a future custom accent is added. */
  accentColor?: string;
  showTitle: boolean;
  showFooter: boolean;
  showBorder: boolean;
  /**
   * Render an attribution line beneath the iframe in the COPIED SNIPPET.
   *
   * An iframe is not a link. A widget pasted into a LibGuide, a Canvas page, or a
   * WordPress post therefore credits nothing and passes nothing back, and any anchor
   * inside the widget is served from this origin, so it links the site to itself. The
   * attribution line is the only part of an embed that lives in the HOST page's HTML,
   * which is why this flag never reaches the widget URL and `appearanceSearchParams`
   * deliberately omits it.
   *
   * The anchor text is the brand and the descriptive words sit outside it. Keyword-rich
   * widget anchors repeated across every host are the pattern Google's link-spam
   * guidance discounts; a brand anchor with context around it is ordinary attribution.
   * Publishers can also switch it off, which is the other half of that guidance.
   */
  attribution: boolean;
  height: number;
  width: "full" | number;
  autoResize: boolean;
}

export interface EmbedConfig {
  locale: string;
  target: EmbedTarget;
  appearance: EmbedAppearance;
  /** Prefill the auto-detect input, same contract as `?source=` on the site. */
  source?: string;
}

export interface EmbedResizeMessage {
  source: typeof EMBED_MESSAGE_SOURCE;
  type: typeof EMBED_RESIZE_TYPE;
  height: number;
}

export function isEmbedKind(value: unknown): value is EmbedKind {
  return (
    typeof value === "string" &&
    (EMBED_KINDS as readonly string[]).includes(value)
  );
}

export function isEmbedTheme(value: unknown): value is EmbedTheme {
  return (
    typeof value === "string" &&
    (EMBED_THEMES as readonly string[]).includes(value)
  );
}

export function isEmbedChrome(value: unknown): value is EmbedChrome {
  return (
    typeof value === "string" &&
    (EMBED_CHROMES as readonly string[]).includes(value)
  );
}

export function isEmbedDensity(value: unknown): value is EmbedDensity {
  return (
    typeof value === "string" &&
    (EMBED_DENSITIES as readonly string[]).includes(value)
  );
}

export function isEmbedRadius(value: unknown): value is EmbedRadius {
  return (
    typeof value === "string" &&
    (EMBED_RADII as readonly string[]).includes(value)
  );
}

export function isEmbedAccent(value: unknown): value is EmbedAccent {
  return (
    typeof value === "string" &&
    (EMBED_ACCENTS as readonly string[]).includes(value)
  );
}

export function isEmbedSlug(value: unknown): value is string {
  return typeof value === "string" && SLUG_PATTERN.test(value);
}

export function defaultEmbedAppearance(): EmbedAppearance {
  return {
    theme: "system",
    chrome: "branded",
    density: "comfortable",
    radius: "lg",
    accent: "brand",
    showTitle: true,
    showFooter: true,
    showBorder: false,
    attribution: true,
    height: EMBED_HEIGHT_DEFAULT,
    width: "full",
    autoResize: true,
  };
}

export function clampEmbedHeight(value: number): number {
  if (!Number.isFinite(value)) {
    return EMBED_HEIGHT_DEFAULT;
  }
  return Math.min(
    EMBED_HEIGHT_MAX,
    Math.max(EMBED_HEIGHT_MIN, Math.round(value))
  );
}

export function parseEmbedWidth(value: unknown): "full" | number {
  if (value === "full" || value === "100%" || value == null || value === "") {
    return "full";
  }
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) {
    return "full";
  }
  return Math.min(
    EMBED_WIDTH_MAX,
    Math.max(EMBED_WIDTH_MIN, Math.round(numeric))
  );
}

export function sanitizeHexColor(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const match = HEX_COLOR_PATTERN.exec(value.trim());
  if (!match?.[1]) {
    return undefined;
  }
  return match[1].toLowerCase();
}

export function escapeHtmlAttr(value: string): string {
  return value.replace(
    HTML_ATTR_ESCAPE,
    (char) => HTML_ATTR_REPLACEMENTS[char] ?? char
  );
}

export function normalizeEmbedLocale(value: unknown): string {
  if (typeof value === "string" && LOCALE_CODE_PATTERN.test(value)) {
    return value;
  }
  return DEFAULT_LOCALE;
}

function booleanParam(value: unknown, fallback: boolean): boolean {
  if (value === "0" || value === "false" || value === false || value === 0) {
    return false;
  }
  if (value === "1" || value === "true" || value === true || value === 1) {
    return true;
  }
  return fallback;
}

function pick<T>(
  value: unknown,
  guard: (candidate: unknown) => candidate is T,
  fallback: T
): T {
  return guard(value) ? value : fallback;
}

export function parseEmbedTarget(
  kind: unknown,
  slug: unknown
): EmbedTarget | null {
  if (!isEmbedKind(kind)) {
    return null;
  }
  if (kind === "generator" || kind === "bibliography") {
    return { kind };
  }
  if (!isEmbedSlug(slug)) {
    return null;
  }
  return { kind, slug };
}

export function parseEmbedPathSegments(
  segments: readonly string[] | undefined
): EmbedTarget | null {
  if (!segments || segments.length === 0) {
    return { kind: "generator" };
  }
  const [kind, slug] = segments;
  if (kind === "generator" && segments.length === 1) {
    return { kind: "generator" };
  }
  if (kind === "bibliography" && segments.length === 1) {
    return { kind: "bibliography" };
  }
  if ((kind === "style" || kind === "tool") && slug && segments.length === 2) {
    return parseEmbedTarget(kind, slug);
  }
  return null;
}

export function parseEmbedAppearance(
  params: URLSearchParams | Record<string, string | undefined>
): EmbedAppearance {
  const get =
    params instanceof URLSearchParams
      ? (key: string) => params.get(key) ?? undefined
      : (key: string) => params[key];
  const defaults = defaultEmbedAppearance();
  const heightRaw = get("height");
  const height =
    heightRaw == null || heightRaw === ""
      ? defaults.height
      : clampEmbedHeight(Number(heightRaw));

  return {
    theme: pick(get("theme"), isEmbedTheme, defaults.theme),
    chrome: pick(get("chrome"), isEmbedChrome, defaults.chrome),
    density: pick(get("density"), isEmbedDensity, defaults.density),
    radius: pick(get("radius"), isEmbedRadius, defaults.radius),
    accent: pick(get("accent"), isEmbedAccent, defaults.accent),
    accentColor: sanitizeHexColor(get("accentColor")),
    showTitle: booleanParam(get("title"), defaults.showTitle),
    showFooter: booleanParam(get("footer"), defaults.showFooter),
    showBorder: booleanParam(get("border"), defaults.showBorder),
    attribution: booleanParam(get("attribution"), defaults.attribution),
    height,
    width: parseEmbedWidth(get("width")),
    autoResize: booleanParam(get("autoresize"), defaults.autoResize),
  };
}

export function parseEmbedSource(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > 2000) {
    return undefined;
  }
  if (SOURCE_UNSAFE_PATTERN.test(trimmed)) {
    return undefined;
  }
  return trimmed;
}

export function parseEmbedConfig(input: {
  locale?: unknown;
  segments?: readonly string[];
  searchParams?: URLSearchParams | Record<string, string | undefined>;
}): EmbedConfig | null {
  const target = parseEmbedPathSegments(input.segments);
  if (!target) {
    return null;
  }
  const search = input.searchParams ?? {};
  const get =
    search instanceof URLSearchParams
      ? (key: string) => search.get(key) ?? undefined
      : (key: string) => search[key];
  return {
    locale: normalizeEmbedLocale(input.locale),
    target,
    appearance: parseEmbedAppearance(search),
    source: parseEmbedSource(get("source")),
  };
}

export function embedWidgetPath(
  config: Pick<EmbedConfig, "locale" | "target">
): string {
  const { kind, slug } = config.target;
  const base = `/${config.locale}/embed/widget`;
  if (kind === "generator") {
    return base;
  }
  if (kind === "bibliography") {
    return `${base}/bibliography`;
  }
  return `${base}/${kind}/${slug}`;
}

export function embedStudioPath(locale = DEFAULT_LOCALE): string {
  return `/${locale}/embed`;
}

/**
 * The public page an embedded target stands for, unprefixed.
 *
 * Derived structurally rather than from the catalog: this module is imported by the
 * client bundle and must not pull in ~10,800 style records. Callers that already hold
 * a resolved target pass its `pagePath` to `buildIframeSnippet` instead, which is how
 * a landing style links to `/apa` rather than to `/citation-style/apa`. Both pages
 * exist, so the fallback is correct even when it is not the prettiest URL.
 */
export function embedAttributionPath(target: EmbedTarget): string {
  if (target.kind === "bibliography") {
    return "/bibliography";
  }
  if (target.kind === "style" && target.slug) {
    return `/citation-style/${target.slug}`;
  }
  if (target.kind === "tool" && target.slug) {
    return `/tools/${target.slug}`;
  }
  return "/";
}

/** Absolute, locale-prefixed href for the attribution anchor. */
export function embedAttributionHref(
  config: Pick<EmbedConfig, "locale" | "target">,
  origin = EMBED_SITE_URL,
  pagePath?: string
): string {
  const path = pagePath ?? embedAttributionPath(config.target);
  const suffix = path === "/" ? "" : path;
  return `${origin}/${config.locale}${suffix}`;
}

/*
 * `attribution` is absent from this table on purpose.
 *
 * Every other appearance key changes what the widget renders, so it belongs in the
 * widget URL. The attribution line renders in the host page, outside the iframe, and
 * the widget would have nothing to do with the flag if it received it. Adding it here
 * would put a meaningless parameter on every embed URL.
 */
function appearanceSearchParams(appearance: EmbedAppearance): URLSearchParams {
  const defaults = defaultEmbedAppearance();
  const params = new URLSearchParams();
  const entries: [
    string,
    string | undefined,
    string | number | boolean | undefined,
  ][] = [
    ["theme", appearance.theme, defaults.theme],
    ["chrome", appearance.chrome, defaults.chrome],
    ["density", appearance.density, defaults.density],
    ["radius", appearance.radius, defaults.radius],
    ["accent", appearance.accent, defaults.accent],
    ["title", appearance.showTitle ? "1" : "0", defaults.showTitle ? "1" : "0"],
    [
      "footer",
      appearance.showFooter ? "1" : "0",
      defaults.showFooter ? "1" : "0",
    ],
    [
      "border",
      appearance.showBorder ? "1" : "0",
      defaults.showBorder ? "1" : "0",
    ],
    ["height", String(appearance.height), String(defaults.height)],
    [
      "width",
      appearance.width === "full" ? undefined : String(appearance.width),
      undefined,
    ],
    [
      "autoresize",
      appearance.autoResize ? "1" : "0",
      defaults.autoResize ? "1" : "0",
    ],
  ];
  for (const [key, value, fallback] of entries) {
    if (value == null || value === fallback) {
      continue;
    }
    params.set(key, value);
  }
  if (appearance.accentColor) {
    params.set("accentColor", appearance.accentColor);
  }
  return params;
}

export function embedWidgetSearchParams(
  config: Pick<EmbedConfig, "appearance" | "source">
): URLSearchParams {
  const params = appearanceSearchParams(config.appearance);
  if (config.source) {
    params.set("source", config.source);
  }
  return params;
}

export function embedWidgetHref(
  config: EmbedConfig,
  origin = EMBED_SITE_URL
): string {
  const path = embedWidgetPath(config);
  const params = embedWidgetSearchParams(config);
  const query = params.toString();
  return query ? `${origin}${path}?${query}` : `${origin}${path}`;
}

export function embedStudioHref(
  config: Pick<EmbedConfig, "locale" | "target">,
  origin = EMBED_SITE_URL
): string {
  const params = new URLSearchParams();
  params.set("kind", config.target.kind);
  if (config.target.slug) {
    params.set("slug", config.target.slug);
  }
  return `${origin}${embedStudioPath(config.locale)}?${params.toString()}`;
}

export function iframeWidthCss(width: EmbedAppearance["width"]): string {
  if (width === "full") {
    return "100%";
  }
  return `${width}px`;
}

export function iframeRadiusCss(radius: EmbedRadius): string {
  return EMBED_RADIUS_CSS[radius];
}

export function resolvedAccentHex(
  appearance: EmbedAppearance
): string | undefined {
  if (appearance.accentColor) {
    return `#${appearance.accentColor}`;
  }
  if (appearance.accent === "brand") {
    return undefined;
  }
  return EMBED_ACCENT_HEX[appearance.accent];
}

/**
 * The credit line that ships with a copied embed.
 *
 * Inline styles, because an LMS rich-text editor strips classes and a bare unstyled
 * paragraph reads like a rendering bug rather than a caption. `rel="noopener"` is a
 * window-handle precaution and does not affect how the link is counted; the anchor is
 * deliberately neither `nofollow` nor `sponsored`, because this is a credit the
 * publisher chose to keep, not a paid placement.
 */
function attributionHtml(
  config: EmbedConfig,
  options: { title: string; origin: string; pagePath?: string }
): string {
  const href = embedAttributionHref(config, options.origin, options.pagePath);
  const context = escapeHtmlAttr(options.title);
  return `\n<p style="margin:8px 0 0;font:14px/1.5 system-ui,-apple-system,Segoe UI,sans-serif;color:#57534e;">${context} by <a href="${escapeHtmlAttr(href)}" rel="noopener" style="color:inherit;text-decoration:underline;" target="_blank">CitationEasy</a></p>`;
}

export function buildIframeSnippet(
  config: EmbedConfig,
  options: { title: string; origin?: string; pagePath?: string }
): string {
  const origin = options.origin ?? EMBED_SITE_URL;
  const src = embedWidgetHref(config, origin);
  const { appearance } = config;
  const width = iframeWidthCss(appearance.width);
  const radius = iframeRadiusCss(appearance.radius);
  const border = appearance.showBorder ? "1px solid #d6d3d1" : "0";
  const title = escapeHtmlAttr(options.title);
  const script = appearance.autoResize
    ? `\n<script src="${origin}${EMBED_SCRIPT_PATH}" async></script>`
    : "";
  const credit = appearance.attribution
    ? attributionHtml(config, {
        title: options.title,
        origin,
        pagePath: options.pagePath,
      })
    : "";
  return `<iframe src="${escapeHtmlAttr(src)}" title="${title}" width="${width}" height="${appearance.height}" loading="lazy" referrerpolicy="strict-origin-when-cross-origin" allow="clipboard-write" style="border:${border};border-radius:${radius};width:${width};max-width:100%;background:transparent;"></iframe>${credit}${script}`;
}

export function isEmbedResizeMessage(
  value: unknown
): value is EmbedResizeMessage {
  if (!value || typeof value !== "object") {
    return false;
  }
  const message = value as Record<string, unknown>;
  return (
    message.source === EMBED_MESSAGE_SOURCE &&
    message.type === EMBED_RESIZE_TYPE &&
    typeof message.height === "number" &&
    Number.isFinite(message.height) &&
    message.height > 0
  );
}

export function buildEmbedResizeMessage(height: number): EmbedResizeMessage {
  return {
    source: EMBED_MESSAGE_SOURCE,
    type: EMBED_RESIZE_TYPE,
    height: Math.max(1, Math.round(height)),
  };
}

export interface OEmbedResponse {
  version: "1.0";
  type: "rich";
  provider_name: "CitationEasy";
  provider_url: string;
  title: string;
  html: string;
  width: number;
  height: number;
  cache_age: number;
}

export function parseOEmbedRequestUrl(url: string): URL | null {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Extract locale + widget segments from a public embed URL. */
export function embedConfigFromWidgetUrl(url: URL): EmbedConfig | null {
  const parts = url.pathname.split("/").filter(Boolean);
  const locale = parts[0];
  if (parts[1] !== "embed" || parts[2] !== "widget") {
    return null;
  }
  return parseEmbedConfig({
    locale,
    segments: parts.slice(3),
    searchParams: url.searchParams,
  });
}

export function oEmbedWidth(
  appearance: EmbedAppearance,
  maxWidth?: number
): number {
  const raw = appearance.width === "full" ? 720 : appearance.width;
  if (maxWidth && maxWidth > 0) {
    return Math.min(raw, maxWidth);
  }
  return raw;
}

export function oEmbedHeight(
  appearance: EmbedAppearance,
  maxHeight?: number
): number {
  if (maxHeight && maxHeight > 0) {
    return Math.min(appearance.height, maxHeight);
  }
  return appearance.height;
}
